import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { twilioOpsApi, type TwilioOpsResponse, type TwilioRecentEntry, type TwilioUsageCategory, type TwilioSiteRow } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";
import { TrustPill } from "../components/TrustPill";

export function TwilioPage() {
  const q = useQuery({
    queryKey: ["ops-twilio"],
    queryFn: twilioOpsApi.status,
    refetchInterval: 5 * 60_000, // 5 min — Twilio API isn't rate-limited heavily but no need to hammer
  });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Phones (Twilio/SignalWire)</h1>
          <p className="text-ink-600 text-sm mt-1">
            Per-site tracking number status, account health, and call activity. Voicemail-by-default for lead-gen sites; flip to forward via{" "}
            <code className="bg-ink-100 px-1 rounded text-xs">rankforge twilio mode --domain X --set forward</code>{" "}
            on lease.{" "}
            <Link to="/ops" className="text-accent hover:underline">Ops</Link>{" · "}
            <Link to="/ops/finance" className="text-accent hover:underline">Finance</Link>
          </p>
        </div>
      </div>

      {q.isLoading && <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((q.error as Error).message)}
        </div>
      )}
      {q.data && <Body data={q.data} />}
    </div>
  );
}

function Body({ data }: { data: TwilioOpsResponse }) {
  const burnRateUsd = data.usage.totalUsd + data.inventory.monthlyRentalCents / 100;
  const monthsOfRunway = data.account?.balanceUsd != null && burnRateUsd > 0
    ? data.account.balanceUsd / burnRateUsd
    : null;

  return (
    <div className="space-y-6">
      {/* ── Account card ── */}
      {data.account ? (
        <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
          <h2 className="font-semibold mb-3">Account</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Stat label="Tier" value={data.account.type ?? "?"} accent={data.account.type === "Trial"} />
            <Stat label="Status" value={data.account.status ?? "?"} positive={data.account.status === "active"} warn={data.account.status === "suspended"} />
            <Stat
              label="Balance"
              value={data.account.balanceUsd != null ? `$${data.account.balanceUsd.toFixed(2)}` : "—"}
              warn={data.account.balanceUsd != null && data.account.balanceUsd < 5}
            />
            <Stat
              label="30d burn"
              value={`$${(data.usage.totalUsd + data.inventory.monthlyRentalCents / 100).toFixed(2)}`}
              hint={`${data.usage.totalUsd.toFixed(2)} usage + ${(data.inventory.monthlyRentalCents / 100).toFixed(2)} rentals`}
            />
            <Stat
              label="Runway"
              value={monthsOfRunway != null ? `${monthsOfRunway.toFixed(1)}mo` : "—"}
              warn={monthsOfRunway != null && monthsOfRunway < 1}
            />
          </div>
          {data.account.sid && (
            <p className="text-xs text-ink-400 mt-3 font-mono">SID: {data.account.sid.slice(0, 10)}…{data.account.sid.slice(-4)}</p>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
          <strong>Twilio account API unavailable</strong> — TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN may not be set as worker secrets, or the account API call failed. Set via{" "}
          <code className="bg-white px-1 rounded text-xs">wrangler secret put TWILIO_ACCOUNT_SID</code>{" "}from inside <code>packages/server/</code>.
        </div>
      )}

      {/* ── Inventory card ── */}
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
        <h2 className="font-semibold mb-3">Inventory</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <Stat label="Provisioned" value={`${data.inventory.provisioned} / ${data.inventory.totalSites}`} positive={data.inventory.provisioned > 0} />
          <Stat label="Voicemail mode" value={data.inventory.byMode.voicemail} />
          <Stat label="Forward mode" value={data.inventory.byMode.forward} positive={data.inventory.byMode.forward > 0} />
          <Stat label="Monthly rental" value={`$${(data.inventory.monthlyRentalCents / 100).toFixed(2)}`} hint="from recurring_costs" />
        </div>
        <div className="text-xs text-ink-500 flex flex-wrap gap-x-4 gap-y-1">
          <span>Provider mix: <strong className="text-ink-700">Twilio: {data.inventory.byProvider.twilio}</strong>{data.inventory.byProvider.signalwire > 0 && <> · SignalWire: {data.inventory.byProvider.signalwire}</>}</span>
          {data.inventory.unprovisioned > 0 && <span>Unprovisioned: <strong className="text-amber-700">{data.inventory.unprovisioned}</strong></span>}
        </div>
      </div>

      {/* ── Webhook health card ── */}
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          Webhook health <TrustPill label={data.dataIntegrity?.webhookHealth} />
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Stat label="Inbound 24h" value={data.webhookHealth.activityLast24h} positive={data.webhookHealth.activityLast24h > 0} />
          <Stat label="Inbound 7d" value={data.webhookHealth.activityLast7d} positive={data.webhookHealth.activityLast7d > 0} />
          <Stat label="Endpoint" value="✓ live" hint="rankforge-leads.workers.dev" positive />
        </div>
        <p className="text-xs text-ink-400 mt-3">Webhook signature verification happens per-request inside the Worker; failures aren't yet logged separately. <strong className="text-ink-600">Future:</strong> add `webhook_events` table for per-call success/fail visibility. Counts exclude is_test=true (Officer Kim sentinels + operator self-tests).</p>
      </div>

      {/* ── Usage breakdown ── */}
      {data.usage.categories.length > 0 && (
        <UsageTable categories={data.usage.categories} totalUsd={data.usage.totalUsd} />
      )}

      {/* ── Per-site activity ── */}
      <PerSiteTwilioTable sites={data.sites} />

      {/* ── Recent activity ── */}
      {data.recent.length > 0 && (
        <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            Recent activity <TrustPill label={data.dataIntegrity?.recent} />
          </h2>
          <div className="space-y-2">
            {data.recent.map((r) => <RecentRow key={r.id} entry={r} />)}
          </div>
        </div>
      )}
      {data.recent.length === 0 && (
        <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm text-sm text-ink-500">
          <h2 className="font-semibold text-ink-900 mb-1 flex items-center gap-2">
            Recent activity <TrustPill label={data.dataIntegrity?.recent} />
          </h2>
          No real call/SMS/voicemail activity in the current window. Test
          rows and Officer Kim sentinels are filtered out — append
          <code className="mx-1 px-1 bg-ink-100 rounded">?showTests=true</code>
          to the URL for the audit view.
        </div>
      )}

      {/* ── Quick reference ── */}
      <div className="bg-ink-50 border border-ink-100 rounded-lg p-4 text-sm text-ink-600">
        <p className="font-semibold mb-2">Quick reference</p>
        <ul className="space-y-1 text-xs">
          <li><code className="bg-white px-1 rounded">rankforge twilio provision --domain X --no-dry-run</code> — buy + configure tracking number for one site</li>
          <li><code className="bg-white px-1 rounded">rankforge twilio mode --domain X --set forward --forward-to +1...</code> — flip mode (e.g. on lease)</li>
          <li><code className="bg-white px-1 rounded">rankforge twilio greeting --domain X</code> — show / regenerate per-site voicemail greeting</li>
          <li><code className="bg-white px-1 rounded">rankforge twilio release --domain X --no-dry-run</code> — release a number (Twilio stops billing)</li>
          <li><code className="bg-white px-1 rounded">node packages/cli/scripts/bulk-twilio-provision.mjs --no-dry-run</code> — provision every unprovisioned live site</li>
        </ul>
      </div>
    </div>
  );
}

function UsageTable({ categories, totalUsd }: { categories: TwilioUsageCategory[]; totalUsd: number }) {
  // Slice to top-12 first (matching original behavior), then sort.
  const visible = categories.slice(0, 12);
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<TwilioUsageCategory>(
    visible,
    {
      category: (c) => c.description || c.category,
      units: (c) => c.count,
      cost: (c) => c.priceUsd,
    },
    { key: "cost", dir: "desc" },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <h2 className="font-semibold mb-3">Usage by category (last 30d)</h2>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
          <tr>
            <SortHeader label="Category" sortKey="category" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left pb-2" />
            <SortHeader label="Units" sortKey="units" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right pb-2" />
            <SortHeader label="Cost (USD)" sortKey="cost" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right pb-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-50">
          {sortedRows.map((c) => (
            <tr key={c.category}>
              <td className="py-2 text-ink-700">{c.description || c.category}</td>
              <td className="py-2 text-right tabular-nums text-ink-600">{c.count.toLocaleString()} {c.countUnit}</td>
              <td className="py-2 text-right tabular-nums font-mono">${c.priceUsd.toFixed(4)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t border-ink-100">
          <tr>
            <td className="pt-2 font-semibold">Total</td>
            <td></td>
            <td className="pt-2 text-right tabular-nums font-mono font-semibold">${totalUsd.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function PerSiteTwilioTable({ sites }: { sites: TwilioSiteRow[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<TwilioSiteRow>(
    sites,
    {
      domain: (s) => s.domain,
      phone: (s) => s.phoneNumber,
      mode: (s) => s.callMode,
      calls: (s) => s.recentCalls,
      voicemails: (s) => s.recentVoicemails,
      sms: (s) => s.recentSmses,
      provisioned: (s) => s.provisionedAt,
    },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-ink-100">
        <h2 className="font-semibold">Per-site (last 28d)</h2>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
          <tr>
            <SortHeader label="Domain" sortKey="domain" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Phone" sortKey="phone" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Mode" sortKey="mode" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
            <SortHeader label="Calls" sortKey="calls" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" />
            <SortHeader label="Voicemails" sortKey="voicemails" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" />
            <SortHeader label="SMS" sortKey="sms" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" />
            <SortHeader label="Provisioned" sortKey="provisioned" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {sortedRows.map((s) => (
            <tr key={s.domain} className="hover:bg-ink-50/50">
              <td className="px-4 py-3 font-medium text-ink-900">{s.domain}</td>
              <td className="px-4 py-3 font-mono text-xs text-ink-700">{s.phoneNumber ? formatUsPhone(s.phoneNumber) : <span className="text-ink-400">—</span>}</td>
              <td className="px-4 py-3 text-xs">
                {s.callMode ? (
                  <span className={`uppercase tracking-wide ${s.callMode === "voicemail" ? "text-purple-700" : s.callMode === "forward" ? "text-green-700" : "text-ink-500"}`}>
                    {s.callMode}
                  </span>
                ) : <span className="text-ink-400">—</span>}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">
                {s.recentCalls > 0 ? <strong>{s.recentCalls}</strong> : <span className="text-ink-400">0</span>}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">
                {s.recentVoicemails > 0 ? <strong className="text-purple-700">{s.recentVoicemails}</strong> : <span className="text-ink-400">0</span>}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">
                {s.recentSmses > 0 ? <strong className="text-blue-700">{s.recentSmses}</strong> : <span className="text-ink-400">0</span>}
              </td>
              <td className="px-4 py-3 text-xs text-ink-500">
                {s.provisionedAt ? new Date(s.provisionedAt).toLocaleDateString() : <span className="text-ink-400">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, positive, warn, accent, hint }: {
  label: string; value: string | number;
  positive?: boolean; warn?: boolean; accent?: boolean; hint?: string;
}) {
  const color = warn ? "text-amber-700" : positive ? "text-green-700" : accent ? "text-purple-700" : "text-ink-900";
  return (
    <div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      <div className="text-[11px] text-ink-500 uppercase tracking-wide" title={hint}>{label}</div>
      {hint && <div className="text-[10px] text-ink-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function RecentRow({ entry }: { entry: TwilioRecentEntry }) {
  const verb = entry.type === "voicemail" ? "Voicemail" : entry.type === "sms" ? "SMS" : "Call";
  const verbColor = entry.type === "voicemail" ? "text-purple-700" : entry.type === "sms" ? "text-blue-700" : "text-green-700";
  const dur = entry.durationSeconds != null && entry.durationSeconds > 0
    ? ` · ${Math.floor(entry.durationSeconds / 60)}m ${entry.durationSeconds % 60}s` : "";
  const when = new Date(entry.receivedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="flex items-baseline gap-3 text-xs border-b border-ink-50 pb-2 last:border-0">
      <span className={`font-medium ${verbColor} w-20`}>{verb}</span>
      <span className="text-ink-700">{entry.callerPhone ?? "(unknown)"}{dur}</span>
      <span className="text-ink-500">→</span>
      <Link to={`/sites/${entry.siteId}`} className="text-accent hover:underline">{entry.siteDomain}</Link>
      {entry.message && <span className="text-ink-500 italic truncate max-w-md">"{entry.message.slice(0, 100)}"</span>}
      <span className="text-ink-400 ml-auto whitespace-nowrap">{when}</span>
    </div>
  );
}

function formatUsPhone(e164: string): string {
  const m = /^\+1?(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}
